import axios from 'axios';
import { db } from '../firebase';
import { getDoc, doc } from 'firebase/firestore';
import { LiveGame, LiveGamesResponse } from '../const/games';
import { loadTeamData } from './TeamService';

export type League = 'mlb' | 'nba' | 'nhl' | 'nfl';

const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001`;

const TEAM_DATA: Record<League, any[]> = {
  mlb: [],
  nba: [],
  nhl: [],
  nfl: [],
};

export async function preloadTeamData() {
  const leagues: League[] = ['mlb', 'nba', 'nhl', 'nfl'];
  for (const league of leagues) {
    try {
      TEAM_DATA[league] = await loadTeamData(league);
    } catch (err) {
      const error = err as Error;
      console.error(`❌ Failed to preload team data for ${league}:`, error.message);
    }
  }
}

export async function fetchLiveGameSummary(league: League, eventId: string) {
  try {
    const eventDoc = doc(db, 'events', `${league}_${eventId}`);
    const eventSnap = await getDoc(eventDoc);

    if (eventSnap.exists()) {
      console.log(`✅ Loaded event ${league}_${eventId} from Firestore`);
      return eventSnap.data();
    }
  } catch (err) {
    console.warn(`⚠️ Firestore error loading event:`, (err as Error).message);
  }

  // fallback if not found
  const res = await axios.get(`${API_BASE}/summary/${league}/${eventId}`);
  return res.data;
}


export async function fetchLiveGames(
  league: League,
  day: 'yesterday' | 'today' | 'upcoming' = 'today'
): Promise<LiveGamesResponse> {
  const url = `${API_BASE}/schedule/${league}/${day}`;
  try {
    const res = await axios.get(url);
    if (res.data?.status === 'offseason') {
      return { status: 'offseason', message: 'Season is over.' };
    }
    return parseEvents(res.data || [], league);
  } catch (err) {
    console.error(`❌ Failed to fetch schedule ${league}/${day}:`, (err as Error).message);
    throw err;
  }
}


export async function fetchAllClutchScores(league: string): Promise<{ updatedAt: number, results: any[] }> {
  const res = await axios.get(`${API_BASE}/hype/${league}`);
  return res.data; // <-- not Map, not messing with it
}


export async function fetchTeamData(league: League) {
  const ref = doc(db, 'meta', `teams_${league}`);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    throw new Error(`No teams metadata found for ${league}`);
  }

  const teams = snap.data()?.teams ?? [];
  const map: Record<string, any> = {};

  for (const team of teams) {
    map[team.teamId] = team;
  }

  return map;
}


// Parsing logic
function parseEvents(events: any[], league: League): LiveGame[] {
  const teamList = TEAM_DATA[league];

  return events.map((game: any): LiveGame => {
    const away = teamList.find(t => t.teamName.toLowerCase() === game.awayTeamName.toLowerCase());
    const home = teamList.find(t => t.teamName.toLowerCase() === game.homeTeamName.toLowerCase());

    const storedMoneylineCache = localStorage.getItem('moneylineCache');
    const moneylineCache = new Map<string, string>(
      storedMoneylineCache ? JSON.parse(storedMoneylineCache) : []
    );

    const matchupKey = `${league}-${away?.teamId || game.awayTeamName}-${home?.teamId || game.homeTeamName}`;

    if (game.line && !moneylineCache.has(matchupKey)) {
      moneylineCache.set(matchupKey, game.line);
      localStorage.setItem('moneylineCache', JSON.stringify(Array.from(moneylineCache.entries())));
    }

    return {
      id: `${league}-${game.date}-${away?.teamId || game.awayTeamName}-${home?.teamId || game.homeTeamName}`,
      eventId: game.eventId,
      league: league.toUpperCase(),
      playoffNote: game.playoffNote ?? '',
      seriesInfo: game.seriesInfo ?? '',
      line: moneylineCache.get(matchupKey) ?? '',
      overUnder: game.overUnder ?? '',
      homeTeam: formatTeam(
        {
          teamId: home?.teamId,
          teamName: home?.teamName,
          color: home?.color,
          logo: home?.logo,
          abbreviation: home?.abbreviation,
          linescores: game.homeTeam?.linescores ?? [],
          runs: game.homeTeam?.runs ?? "-",
          hits: game.homeTeam?.hits ?? "-",
          errors: game.homeTeam?.errors ?? "-",
        },
        game.homeTeamName
      ),
    
      awayTeam: formatTeam(
        {
          teamId: away?.teamId,
          teamName: away?.teamName,
          color: away?.color,
          logo: away?.logo,
          abbreviation: away?.abbreviation,
          linescores: game.awayTeam?.linescores ?? [],
          runs: game.awayTeam?.runs ?? "-",
          hits: game.awayTeam?.hits ?? "-",
          errors: game.awayTeam?.errors ?? "-",
        },
        game.awayTeamName
      ),
      homeTeamId: game.homeTeamId,
      awayTeamId: game.awayTeamId,
      time: new Date(game.time).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
      network: game.network,
      status: game.status,
      liveStatus: {
        clock: game.liveStatus?.clock,
        displayClock: game.liveStatus?.displayClock,
        period: game.liveStatus?.period,
        type: {
          id: game.liveStatus?.type?.id,
          name: game.liveStatus?.type?.name,
          state: game.liveStatus?.type?.state,
          completed: game.liveStatus?.type?.completed,
          description: game.liveStatus?.type?.description,
          detail: game.liveStatus?.type?.detail,
          shortDetail: game.liveStatus?.type?.shortDetail,
        },
      },
      date: game.date,
      awayTeamScore: game.awayScore,
      homeTeamScore: game.homeScore,
      winningPitcher: game.winningPitcher ?? '',
      losingPitcher: game.losingPitcher ?? '',
      savePitcher: game.savePitcher ?? '',
      balls: game.balls,
      strikes: game.strikes,
      outs: game.outs,
      pitcher: game.pitcher,
      batter: game.batter,
      onDeck: game.onDeck,
      inning: game.inning,
      inningHalf: game.inningHalf,
      period: game.period,
      occupiedBases: game.occupiedBases,
      venue: game.venue ?? '',
    };
  });
}

function formatTeam(teamData: any, fallbackName: string) {
  if (!teamData) {
    return {
      teamId: fallbackName.toLowerCase(),
      teamName: truncateTeamName(fallbackName),
      color: '#ccc',
      logo: '',
      abbreviation: '',
      linescores: [],
      runs: '-',
      hits: '-',
      errors: '-',
    };
  }

  return {
    teamId: teamData.teamId,
    teamName: truncateTeamName(teamData.teamName ?? fallbackName),
    color: teamData.color ?? '#ccc',
    logo: teamData.logo ?? '',
    abbreviation: teamData.abbreviation ?? '',
    linescores: teamData.linescores ?? [],
    runs: teamData.runs ?? '-',
    hits: teamData.hits ?? '-',
    errors: teamData.errors ?? '-',
  };
}


function truncateTeamName(fullName: string): string {
  const knownPrefixes = [
    'New York',
    'Los Angeles',
    'Kansas City',
    'San Francisco',
    'San Diego',
    'Oklahoma City',
    'Golden State',
  ];

  for (const prefix of knownPrefixes) {
    if (fullName.startsWith(prefix)) {
      return fullName.replace(`${prefix} `, '');
    }
  }

  const parts = fullName.split(' ');
  return parts.length > 1 ? parts.slice(1).join(' ') : fullName;
}
